// Clean GlucoseTrendsCard with annotations - no data fetching, pure component
import React, { useEffect, useRef, useState } from 'react';

// Simple annotation interface
export interface Annotation {
  date: string;  // YYYY-MM-DD
  icon: string;  // emoji
  note: string;  // description
}

// Glucose reading interface (matches existing prop structure)
export interface GlucoseReading {
  value: number;
  type: string;
  date: Date;
}

// Glucose type colors and labels
const TYPE_COLORS: Record<string, string> = {
  fasting: '#f59e0b',      // orange
  'post-meal': '#8b5cf6',  // purple
  'pre-meal': '#f97316',   // orange-red
  random: '#6b7280',       // gray
};

const TYPE_LABELS: Record<string, string> = {
  fasting: 'Fasting',
  'post-meal': 'Post-Meal',
  'pre-meal': 'Pre-Meal',
  random: 'Random',
};

const FILTERS = [
  { key: 'all', label: 'All Types' },
  { key: 'fasting', label: 'Fasting' },
  { key: 'post-meal', label: 'Post-Meal' },
  { key: 'pre-meal', label: 'Pre-Meal' },
];

// Utility functions
function formatDate(dt: Date) {
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatTime(dt: Date) {
  let h = dt.getHours();
  const m = dt.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
}

// Parse YYYY-MM-DD as local date (not UTC)
function parseLocalDate(dateStr: string) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const CARD_STYLE = 'w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 p-4 sm:p-8 mb-8';

interface GlucoseTrendsCardProps {
  readings: GlucoseReading[];    // Required - from CtankHealthSummary
  annotations: Annotation[];     // Required - from CtankHealthSummary
}

const GlucoseTrendsCard: React.FC<GlucoseTrendsCardProps> = ({ 
  readings, 
  annotations 
}) => {
  const [filter, setFilter] = useState('all');
  const [hovered, setHovered] = useState<number | null>(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 350 });
  const [tooltip, setTooltip] = useState<null | {
    x: number;
    y: number;
    value?: number;
    type?: string;
    date: Date;
    idx: number;
    annotation?: Annotation;
  }>(null);
  
  const svgRef = useRef<SVGSVGElement>(null);

  // Responsive resize
  useEffect(() => {
    function handleResize() {
      if (svgRef.current) {
        const parent = svgRef.current.parentElement;
        if (parent) {
          setDimensions({
            width: parent.clientWidth,
            height: 350,
          });
        }
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter data based on selected type
  const filtered = filter === 'all' ? readings : readings.filter(d => d.type === filter);

  // Show empty state if no data
  if (readings.length === 0) {
    return (
      <div className={CARD_STYLE} style={{ width: '100%' }}>
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">No glucose data available for the last 30 days.</div>
        </div>
      </div>
    );
  }

  // Chart dimensions
  const { width, height } = dimensions;
  const PAD = 60;
  const chartWidth = width - 2 * PAD;
  const chartHeight = height - 2 * PAD;

  // Scales
  const minDate = Math.min(...readings.map(d => d.date.getTime()));
  const maxDate = Math.max(...readings.map(d => d.date.getTime()));
  const dateRange = maxDate - minDate || 1;
  
  const xScale = (timestamp: number) => PAD + ((timestamp - minDate) / dateRange) * chartWidth;
  
  const minY = 80;
  const maxY = 200;
  const yRange = maxY - minY;
  const yScale = (value: number) => height - PAD - ((value - minY) / yRange) * chartHeight;

  // Generate Y-axis ticks
  const yTicks = [80, 100, 120, 130, 150, 180, 200];

  // Filter annotations for the current date range
  const dateStart = new Date(minDate);
  const dateEnd = new Date(maxDate);
  const filteredAnnotations = annotations.filter(annotation => {
    const annotationDate = parseLocalDate(annotation.date);
    return annotationDate >= dateStart && annotationDate <= dateEnd;
  });

  // Mouse event handlers
  function handlePointMouseEnter(idx: number, e: React.MouseEvent) {
    setHovered(idx);
    const d = filtered[idx];
    setTooltip({
      x: xScale(d.date.getTime()),
      y: yScale(d.value),
      value: d.value,
      type: d.type,
      date: d.date,
      idx,
    });
  }

  function handlePointMouseLeave() {
    setHovered(null);
    setTooltip(null);
  }

  function handleAnnotationMouseEnter(annotation: Annotation, e: React.MouseEvent) {
    const annotationDate = parseLocalDate(annotation.date);
    const x = xScale(annotationDate.getTime());
    setTooltip({
      x,
      y: 40,
      date: annotationDate,
      idx: -1,
      annotation,
    });
  }

  function handleAnnotationMouseLeave() {
    setTooltip(null);
  }

  return (
    <div className={CARD_STYLE} style={{ width: '100%' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <span style={{ color: '#ef4444', fontSize: 22 }}>●</span>
          <span>Glucose Trends - Last 30 Days</span>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-1 sm:flex-nowrap overflow-x-auto">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`px-3 py-1 rounded-full border text-sm font-medium transition-all duration-150 ${
                filter === f.key
                  ? 'bg-blue-600 text-white border-blue-600 shadow'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-blue-50'
              }`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="w-full min-w-0 h-[350px] relative">
        <svg
          ref={svgRef}
          width={width}
          height={height}
          className="block w-full min-w-0 h-[350px]"
          style={{ overflow: 'visible' }}
        >
          {/* Grid lines and colored target lines */}
          {yTicks.map(y => {
            // Color and dash for target lines
            let stroke = '#e5e7eb';
            let strokeDasharray = '2 2';
            let strokeWidth = 1;
            if (y === 130) {
              stroke = '#f59e0b'; // orange for fasting target
              strokeDasharray = '6 4';
              strokeWidth = 2;
            } else if (y === 180) {
              stroke = '#8b5cf6'; // purple for post-meal target
              strokeDasharray = '6 4';
              strokeWidth = 2;
            }
            return (
              <line
                key={y}
                x1={PAD}
                x2={width - PAD}
                y1={yScale(y)}
                y2={yScale(y)}
                stroke={stroke}
                strokeDasharray={strokeDasharray}
                strokeWidth={strokeWidth}
              />
            );
          })}

          {/* Y-axis labels */}
          {yTicks.map(y => (
            <text
              key={y}
              x={PAD - 10}
              y={yScale(y) + 4}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              {y}
            </text>
          ))}

          {/* Annotation markers (behind the data points) */}
          {filteredAnnotations.map((annotation, idx) => {
            const annotationDate = parseLocalDate(annotation.date);
            const x = xScale(annotationDate.getTime());
            
            return (
              <g key={idx} className="annotation-marker">
                {/* Vertical line */}
                <line
                  x1={x}
                  x2={x}
                  y1={PAD - 20}
                  y2={height - PAD}
                  stroke="#dc2626"
                  strokeWidth="2"
                  strokeDasharray="3 3"
                  opacity="0.7"
                />
                
                {/* Icon */}
                <text
                  x={x}
                  y={PAD - 25}
                  textAnchor="middle"
                  fontSize="16"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => handleAnnotationMouseEnter(annotation, e)}
                  onMouseLeave={handleAnnotationMouseLeave}
                >
                  {annotation.icon}
                </text>
                
                {/* Date label */}
                <text
                  x={x}
                  y={PAD - 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#dc2626"
                  fontWeight="600"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => handleAnnotationMouseEnter(annotation, e)}
                  onMouseLeave={handleAnnotationMouseLeave}
                >
                  {formatDate(parseLocalDate(annotation.date))}
                </text>
              </g>
            );
          })}

          {/* Single continuous connecting line */}
          {filtered.length > 1 && (() => {
            const sortedData = [...filtered].sort((a, b) => a.date.getTime() - b.date.getTime());
            const pathData = sortedData.map((d, i) => {
              const x = xScale(d.date.getTime());
              const y = yScale(d.value);
              return i === 0 ? `M${x},${y}` : `L${x},${y}`;
            }).join(' ');
            
            return (
              <path
                d={pathData}
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                opacity="0.6"
                style={{ pointerEvents: 'none' }}
              />
            );
          })()}

          {/* Data points */}
          {filtered.map((d, i) => (
            <circle
              key={i}
              cx={xScale(d.date.getTime())}
              cy={yScale(d.value)}
              r={hovered === i ? 6 : 4}
              fill={TYPE_COLORS[d.type]}
              stroke="#fff"
              strokeWidth={2}
              style={{ 
                cursor: 'pointer', 
                filter: hovered === i ? 'drop-shadow(0 2px 6px #0002)' : undefined,
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => handlePointMouseEnter(i, e)}
              onMouseLeave={handlePointMouseLeave}
            />
          ))}

          {/* X-axis labels */}
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map(ratio => {
            const timestamp = minDate + ratio * dateRange;
            const x = PAD + ratio * chartWidth;
            const date = new Date(timestamp);
            return (
              <text
                key={ratio}
                x={x}
                y={height - PAD + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#6b7280"
              >
                {formatDate(date)}
              </text>
            );
          })}

          {/* Y-axis title */}
          <text
            x={20}
            y={height / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            fontWeight="500"
            transform={`rotate(-90 20 ${height / 2})`}
          >
            mg/dL
          </text>

          {/* X-axis title */}
          <text
            x={width / 2}
            y={height - 10}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            fontWeight="500"
          >
            Date
          </text>
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x - 80,
              top: tooltip.y - 80,
              background: 'rgba(0,0,0,0.9)',
              color: 'white',
              border: '1px solid #374151',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              padding: '10px 14px',
              fontSize: 13,
              pointerEvents: 'none',
              zIndex: 10,
              minWidth: 160,
              lineHeight: 1.4,
            }}
          >
            {tooltip.annotation ? (
              // Annotation tooltip
              <>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {tooltip.annotation.icon} {formatDate(tooltip.date)}
                </div>
                <div>{tooltip.annotation.note}</div>
              </>
            ) : (
              // Glucose reading tooltip
              <>
                <div style={{ fontWeight: 600, color: TYPE_COLORS[tooltip.type!] }}>
                  {TYPE_LABELS[tooltip.type!]}
                </div>
                <div>{formatDate(tooltip.date)} {formatTime(tooltip.date)}</div>
                <div style={{ fontWeight: 500 }}>Glucose: {tooltip.value} mg/dL</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Legend - single row, right-aligned target labels */}
      <div className="flex flex-wrap items-center justify-between mt-6 text-xs sm:text-sm w-full">
        <div className="flex flex-wrap gap-6 items-center">
          <div className="flex items-center gap-2">
            <span style={{ width: 16, height: 16, borderRadius: 8, background: '#f59e0b', display: 'inline-block' }}></span>
            Fasting
          </div>
          <div className="flex items-center gap-2">
            <span style={{ width: 16, height: 16, borderRadius: 8, background: '#8b5cf6', display: 'inline-block' }}></span>
            Post-Meal
          </div>
          <div className="flex items-center gap-2">
            <span style={{ width: 16, height: 16, borderRadius: 8, background: '#f97316', display: 'inline-block' }}></span>
            Pre-Meal
          </div>
          <div className="flex items-center gap-2">
            <span style={{ width: 16, height: 16, borderRadius: 8, background: '#6b7280', display: 'inline-block' }}></span>
            Random
          </div>
          <div className="flex items-center gap-2">
            <span style={{ color: '#dc2626', fontSize: 14 }}>📍</span>
            Day Annotations
          </div>
        </div>
        <div className="flex gap-4 items-center ml-auto">
          <span className="flex items-center gap-1">
            <span style={{ width: 28, height: 0, borderTop: '2px dashed #f59e0b', display: 'inline-block' }}></span>
            <span style={{ color: '#f59e0b', fontWeight: 400, fontSize: 12 }}>Fasting Target</span>
          </span>
          <span className="flex items-center gap-1">
            <span style={{ width: 28, height: 0, borderTop: '2px dashed #8b5cf6', display: 'inline-block' }}></span>
            <span style={{ color: '#8b5cf6', fontWeight: 400, fontSize: 12 }}>Post-Meal Target</span>
          </span>
        </div>
      </div>

      {/* Annotations List */}
      {filteredAnnotations.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <div className="font-semibold text-gray-700 mb-3">Day Annotations</div>
          <div className="space-y-2">
            {filteredAnnotations.map((annotation, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <span className="text-lg">{annotation.icon}</span>
                <span className="font-medium text-gray-600">
                  {new Date(annotation.date).toLocaleDateString()}:
                </span>
                <span className="text-gray-800">{annotation.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlucoseTrendsCard;